import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../../../services/auth';
import { totpService } from '../../../../services/totpService';
import AppLayout from '../../../../components/layout/AppLayout/AppLayout.jsx';
import TOTPSetupModal from '../../components/TOTPSetupModal/TOTPSetupModal.jsx';
import TOTPDisableModal from '../../components/TOTPDisableModal/TOTPDisableModal.jsx';
import ProfileEditForm from '../../components/ProfileEditForm/ProfileEditForm.jsx';
import { useDateTime } from '../../../../hooks/useDateTime';
import './ProfilePage.css';

const ProfilePage = ({ onLogout }) => {
  const { t } = useTranslation(['profile', 'common']);
  const { formatDate } = useDateTime();
  const [user, setUser] = useState(null);
  const [totpStatus, setTotpStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Fetch user data and TOTP status
  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get current user
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);

      // Get TOTP status
      const status = await totpService.getStatus();
      setTotpStatus(status);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError(error.response?.data?.message || t('profile:errors.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleEnableTOTP = () => {
    setShowSetupModal(true);
  };

  const handleDisableTOTP = () => {
    setShowDisableModal(true);
  };

  const handleTOTPEnabled = () => {
    setShowSetupModal(false);
    fetchUserData(); // Refresh data
  };

  const handleTOTPDisabled = () => {
    setShowDisableModal(false);
    fetchUserData(); // Refresh data
  };

  const handleModalError = (errorMessage) => {
    setError(errorMessage);
  };

  // Handle edit mode toggle
  const handleEditMode = () => {
    setEditMode(true);
    setError(''); // Clear any errors
  };

  // Handle edit save
  const handleEditSave = (updatedUser) => {
    setUser(updatedUser);
    setEditMode(false);
    setError('');
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditMode(false);
    setError('');
  };

  // Get avatar URL
  const avatarUrl = user?.profile_picture 
    ? authService.getAvatarUrl(user.id) 
    : null;

  if (loading) {
    return (
      <AppLayout 
        title={t('profile:title')}
        error={error}
        setError={setError}
        onLogout={onLogout}
      >
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('profile:loading')}</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout 
      title={t('profile:title')}
      error={error}
      setError={setError}
      onLogout={onLogout}
    >
      <div className="profile-container">
        <div className="profile-content">
          {/* Profile Header */}
          <div className="profile-header">
            <div className="profile-title-section">
              <h1 className="profile-title">{t('profile:title')}</h1>
              <div className="profile-mode-indicator">
                {editMode ? t('profile:edit_mode') : t('profile:view_mode')}
              </div>
            </div>
            {!editMode && (
              <button
                onClick={handleEditMode}
                className="btn btn-primary"
              >
                {t('profile:edit_profile')}
              </button>
            )}
          </div>

          {editMode ? (
            <ProfileEditForm
              user={user}
              onSave={handleEditSave}
              onCancel={handleEditCancel}
              onError={setError}
            />
          ) : (
            <>
              {/* User Information Section */}
              <div className="profile-section">
                <h2 className="section-title">{t('profile:user_information')}</h2>
                
                {/* Avatar Display */}
                {avatarUrl && (
                  <div className="profile-avatar-display">
                    <img 
                      src={avatarUrl} 
                      alt={user.username} 
                      className="profile-avatar"
                    />
                  </div>
                )}

                <div className="user-info-grid">
                  <div className="info-item">
                    <label className="info-label">{t('common:labels.username')}:</label>
                    <span className="info-value">{user?.username}</span>
                  </div>
                  <div className="info-item">
                    <label className="info-label">{t('common:labels.email')}:</label>
                    <span className="info-value">{user?.email}</span>
                  </div>
                  <div className="info-item">
                    <label className="info-label">{t('common:labels.role')}:</label>
                    <span className="info-value role-badge">{user?.role}</span>
                  </div>
                  <div className="info-item">
                    <label className="info-label">{t('common:labels.language')}:</label>
                    <span className="info-value">{user?.language?.toUpperCase()}</span>
                  </div>
                  <div className="info-item">
                    <label className="info-label">{t('common:labels.timezone')}:</label>
                    <span className="info-value">{user?.timezone}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Two-Factor Authentication Section */}
          <div className="profile-section">
            <h2 className="section-title">{t('profile:totp.title')}</h2>
            
            {totpStatus ? (
              <div className="totp-section">
                <div className="totp-status">
                  <div className={`status-indicator ${totpStatus.totp_enabled ? 'enabled' : 'disabled'}`}>
                    <span className="status-icon">
                      {totpStatus.totp_enabled ? '🔒' : '🔓'}
                    </span>
                    <div className="status-text">
                      <p className="status-label">
                        {totpStatus.totp_enabled 
                          ? t('profile:totp.enabled') 
                          : t('profile:totp.disabled')
                        }
                      </p>
                      {totpStatus.totp_enabled && totpStatus.totp_enabled_at && (
                        <p className="status-details">
                          {t('profile:totp.enabled_since', { 
                            date: formatDate(totpStatus.totp_enabled_at)
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="totp-description">
                  <p>
                    {totpStatus.totp_enabled 
                      ? t('profile:totp.description_enabled')
                      : t('profile:totp.description_disabled')
                    }
                  </p>
                </div>

                <div className="totp-actions">
                  {totpStatus.totp_enabled ? (
                    <button
                      onClick={handleDisableTOTP}
                      className="btn btn-danger"
                    >
                      {t('profile:totp.disable_button')}
                    </button>
                  ) : (
                    <button
                      onClick={handleEnableTOTP}
                      className="btn btn-primary"
                    >
                      {t('profile:totp.enable_button')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="totp-loading">
                <p>{t('profile:totp.loading')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TOTP Setup Modal */}
      {showSetupModal && (
        <TOTPSetupModal
          onSuccess={handleTOTPEnabled}
          onCancel={() => setShowSetupModal(false)}
          onError={handleModalError}
        />
      )}

      {/* TOTP Disable Modal */}
      {showDisableModal && (
        <TOTPDisableModal
          onSuccess={handleTOTPDisabled}
          onCancel={() => setShowDisableModal(false)}
          onError={handleModalError}
        />
      )}
    </AppLayout>
  );
};

export default ProfilePage;